// Workers cockpit bar.
//
// Sticky strip at the top of the workers page with the handful of numbers
// an operator actually scans before drilling into a host: how many workers
// are reachable, how many are in the pool, how many sessions are live,
// today's throughput, the fleet's combined disk headroom, and whether anyone
// is behind on the latest Chromium build.
//
// It also carries "Render & Reload": toggling a worker into the pool or nudging
// its weight dirties the nginx config right here, so the control that pushes
// that live belongs on this bar rather than only on the Load Balancer page.

import { Button } from "@veil/ui/components/button";
import { cn } from "@veil/ui/lib/utils";
import Plus from "lucide-react/dist/esm/icons/plus";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import { useMemo } from "react";
import { RenderStatusBar } from "@/components/render-status-bar";
import type { ClusterStats } from "@/pages/cluster/types";
import { countBehindHosts } from "./drift-badge";

interface CommandHost {
	hostname: string;
	enabled: boolean;
	weight: number;
	snapshot: {
		reachable: boolean;
		diskFreeGB: number | null;
		diskTotalGB: number | null;
		installedBuilds: Record<string, string[]> | null;
		latestBuilds: Record<string, string> | null;
		runtime: {
			activeSessions: number;
			sessionsToday: number;
			sessionsLast24h: number;
		} | null;
	} | null;
}

interface Props {
	hosts: CommandHost[];
	loading?: boolean;
	canPoll: boolean;
	canAdd: boolean;
	pollPending: boolean;
	onPoll: () => void;
	onAdd: () => void;
	clusterName: string;
	clusterStats: ClusterStats | undefined;
}

interface Rollups {
	totalHosts: number;
	reachable: number;
	enabled: number;
	liveSessions: number;
	sessionsToday: number;
	sessionsLast24h: number;
	diskFreeGB: number;
	diskTotalGB: number;
	behindCount: number;
}

function computeRollups(hosts: CommandHost[]): Rollups {
	let reachable = 0;
	let enabled = 0;
	let liveSessions = 0;
	let sessionsToday = 0;
	let sessionsLast24h = 0;
	let diskFreeGB = 0;
	let diskTotalGB = 0;
	for (const h of hosts) {
		if (h.enabled) enabled += 1;
		if (h.snapshot?.reachable) {
			reachable += 1;
			liveSessions += h.snapshot.runtime?.activeSessions ?? 0;
			sessionsToday += h.snapshot.runtime?.sessionsToday ?? 0;
			sessionsLast24h += h.snapshot.runtime?.sessionsLast24h ?? 0;
			diskFreeGB += h.snapshot.diskFreeGB ?? 0;
			diskTotalGB += h.snapshot.diskTotalGB ?? 0;
		}
	}
	return {
		totalHosts: hosts.length,
		reachable,
		enabled,
		liveSessions,
		sessionsToday,
		sessionsLast24h,
		diskFreeGB,
		diskTotalGB,
		behindCount: countBehindHosts(hosts),
	};
}

function formatGB(n: number): string {
	if (!Number.isFinite(n) || n <= 0) return "—";
	if (n >= 1000) return `${(n / 1000).toFixed(1)} TB`;
	if (n >= 100) return `${n.toFixed(0)} GB`;
	return `${n.toFixed(1)} GB`;
}

export function WorkersCommandBar({
	hosts,
	loading,
	canPoll,
	canAdd,
	pollPending,
	onPoll,
	onAdd,
	clusterName,
	clusterStats,
}: Props) {
	// Memoize against the array identity. Poll ticks every 30s but
	// React Query keeps stable references when the payload is unchanged,
	// so this avoids recomputing on unrelated re-renders.
	const rollups = useMemo(() => computeRollups(hosts), [hosts]);

	const diskPctUsed =
		rollups.diskTotalGB > 0
			? Math.round(
					((rollups.diskTotalGB - rollups.diskFreeGB) / rollups.diskTotalGB) *
						100,
				)
			: null;

	const driftTone: "ok" | "warn" = rollups.behindCount === 0 ? "ok" : "warn";

	const reachableTone: "ok" | "warn" | "bad" =
		rollups.totalHosts === 0
			? "warn"
			: rollups.reachable === rollups.enabled && rollups.enabled > 0
				? "ok"
				: rollups.reachable === 0
					? "bad"
					: "warn";

	// Same amber wash the Load Balancer bar uses when the live nginx.conf is
	// behind the database — the pool toggles that dirty it now live on this page.
	const dirty = clusterStats?.dirty === true;

	return (
		<header
			className={cn(
				"sticky top-(--header-height) z-10 -mx-4 -mt-4 mb-2 border-b backdrop-blur md:-mx-6 md:-mt-6",
				dirty
					? "border-amber-500/30 bg-amber-50/70 dark:bg-amber-500/[0.04]"
					: "border-border/60 bg-background/95 supports-backdrop-filter:bg-background/70",
			)}
		>
			<div className="flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:gap-6 md:px-6 md:py-4">
				<div className="min-w-0 shrink-0 space-y-1">
					<div className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
						Fleet
					</div>
					<h1 className="font-heading text-xl font-semibold leading-tight tracking-tight">
						Workers
					</h1>
				</div>

				<div className="flex min-w-0 flex-1 flex-wrap items-stretch gap-x-6 gap-y-3 md:justify-end md:gap-x-8">
					<Metric
						label="Reachable"
						value={loading ? "…" : `${rollups.reachable}/${rollups.totalHosts}`}
						tone={reachableTone}
						title={`${rollups.reachable} of ${rollups.totalHosts} workers responded to the last poll · ${rollups.enabled} enabled`}
					/>
					<Metric
						label="In pool"
						value={loading ? "…" : `${rollups.enabled}/${rollups.totalHosts}`}
						title={`${rollups.enabled} of ${rollups.totalHosts} workers are included in the upstream pool`}
					/>
					<Metric
						label="Live sessions"
						value={loading ? "…" : rollups.liveSessions.toString()}
						title={`${rollups.liveSessions} sessions running right now across reachable workers`}
					/>
					<Metric
						label="Sessions today"
						value={loading ? "…" : rollups.sessionsToday.toString()}
						title={`${rollups.sessionsToday} sessions started since midnight · ${rollups.sessionsLast24h} in the last 24h`}
					/>
					<Metric
						label="Disk free"
						value={
							loading
								? "…"
								: rollups.diskTotalGB > 0
									? formatGB(rollups.diskFreeGB)
									: rollups.diskFreeGB > 0
										? formatGB(rollups.diskFreeGB)
										: "—"
						}
						sub={
							rollups.diskTotalGB > 0
								? `of ${formatGB(rollups.diskTotalGB)}${diskPctUsed != null ? ` · ${diskPctUsed}% used` : ""}`
								: undefined
						}
						tone={
							diskPctUsed != null && diskPctUsed >= 90
								? "bad"
								: diskPctUsed != null && diskPctUsed >= 75
									? "warn"
									: "neutral"
						}
						title="Combined disk free across reachable workers"
					/>
					<Metric
						label="Build drift"
						value={
							loading
								? "…"
								: rollups.behindCount === 0
									? "in sync"
									: `${rollups.behindCount} behind`
						}
						tone={driftTone}
						title={
							rollups.behindCount === 0
								? "Every reachable worker has the latest Chromium build it has reported."
								: `${rollups.behindCount} workers are missing the latest Chromium build on at least one major.`
						}
					/>
				</div>

				<RenderStatusBar
					stats={clusterStats}
					clusterName={clusterName}
					className="shrink-0"
				/>

				<div className="flex shrink-0 items-center gap-2 md:ml-2">
					<Button
						size="sm"
						variant="outline"
						onClick={onPoll}
						disabled={!canPoll || pollPending}
						title={
							canPoll ? "Trigger a fleet-wide poll" : "Operator role required"
						}
					>
						<RefreshCcw
							className={cn("mr-2 h-4 w-4", pollPending && "animate-spin")}
						/>
						{pollPending ? "Polling…" : "Poll now"}
					</Button>
					<Button
						size="sm"
						onClick={onAdd}
						disabled={!canAdd}
						title={
							canAdd
								? "Deploy a worker, or register one that is already running"
								: "Operator role required"
						}
					>
						<Plus data-icon="inline-start" /> Add worker
					</Button>
				</div>
			</div>
		</header>
	);
}

function Metric({
	label,
	value,
	sub,
	tone = "neutral",
	title,
}: {
	label: string;
	value: string;
	sub?: string;
	tone?: "ok" | "warn" | "bad" | "neutral";
	title?: string;
}) {
	const toneClass =
		tone === "ok"
			? "text-emerald-700 dark:text-emerald-400"
			: tone === "warn"
				? "text-amber-700 dark:text-amber-400"
				: tone === "bad"
					? "text-destructive"
					: "text-foreground";
	return (
		<div className="flex flex-col justify-center" title={title}>
			<span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
				{label}
			</span>
			<span className={cn("font-mono text-base tabular-nums", toneClass)}>
				{value}
			</span>
			{sub && (
				<span className="font-mono text-[0.68rem] tabular-nums text-muted-foreground">
					{sub}
				</span>
			)}
		</div>
	);
}
